import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import { useAlert } from "../../../../context/AlertContext";
import {
  REASON_LIST_TYPES,
  createReason,
  getReasons,
  reorderReasons,
  updateReason,
} from "../../../../services/reason.service";
import type { IReason, TReasonListType } from "../../../../services/api.types";
import ReasonFormDialog, { ReasonFormValues } from "./ReasonFormDialog";

type ByList = Record<TReasonListType, IReason[]>;

const emptyByList = (): ByList => ({
  customerCancel: [],
  partnerCancel: [],
  reviewTagOwner: [],
  reviewTagUser: [],
});

const group = (items: IReason[]): ByList => {
  const out = emptyByList();
  items.forEach((r) => {
    if (out[r.listType]) out[r.listType].push(r);
  });
  (Object.keys(out) as TReasonListType[]).forEach((k) =>
    out[k].sort((a, b) => a.order - b.order)
  );
  return out;
};

export default function ReasonsSettings() {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const [tab, setTab] = useState<TReasonListType>(REASON_LIST_TYPES[0]);
  const [byList, setByList] = useState<ByList>(emptyByList());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reordering, setReordering] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IReason | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      setByList(group(await getReasons()));
    } catch (e: any) {
      setLoadError(e?.message || t("serviceErrors.serverError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = byList[tab];

  const replaceRow = (updated: IReason) =>
    setByList((prev) => ({
      ...prev,
      [updated.listType]: prev[updated.listType].map((r) => (r._id === updated._id ? updated : r)),
    }));

  const handleToggle = async (r: IReason) => {
    try {
      const updated = await updateReason(r._id, { active: !r.active });
      replaceRow(updated);
      setAlert("success", t("reasons.toast.updated"));
    } catch (e: any) {
      setAlert("error", e?.message || t("serviceErrors.updateReason"));
    }
  };

  const handleMove = async (index: number, delta: number) => {
    const ids = items.map((r) => r._id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setReordering(true);
    try {
      const updated = await reorderReasons(tab, ids);
      setByList((prev) => ({ ...prev, [tab]: [...updated].sort((a, b) => a.order - b.order) }));
    } catch (e: any) {
      setAlert("error", e?.message || t("serviceErrors.reorderReasons"));
    } finally {
      setReordering(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (r: IReason) => {
    setEditing(r);
    setDialogOpen(true);
  };

  const handleSubmit = async ({ value, labels }: ReasonFormValues) => {
    try {
      if (editing) {
        const updated = await updateReason(editing._id, { labels });
        replaceRow(updated);
      } else {
        const created = await createReason({ listType: tab, value, labels });
        setByList((prev) => ({
          ...prev,
          [created.listType]: [...prev[created.listType], created].sort(
            (a, b) => a.order - b.order
          ),
        }));
      }
    } catch (e: any) {
      setAlert("error", e?.message || t("serviceErrors.serverError"));
      throw e;
    }
    setAlert("success", t(editing ? "reasons.toast.updated" : "reasons.toast.created"));
    setDialogOpen(false);
  };

  const summary = (r: IReason) =>
    (["ru", "az", "ge"] as const)
      .map((k) => `${k.toUpperCase()}: ${r.labels[k] || "-"}`)
      .join(" | ");

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Typography variant="h5">{t("reasons.title")}</Typography>
        <Button variant="contained" onClick={openAdd} disabled={loading || !!loadError}>
          {t("reasons.addButton")}
        </Button>
      </div>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" className="mb-4">
        {REASON_LIST_TYPES.map((lt) => (
          <Tab key={lt} value={lt} label={t(`reasons.tabs.${lt}`)} />
        ))}
      </Tabs>

      {loading ? (
        <div className="flex justify-center p-8">
          <CircularProgress />
        </div>
      ) : loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              {t("reasons.retry")}
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : items.length === 0 ? (
        <Paper className="p-6 text-center">
          <Typography color="text.secondary" className="mb-3">
            {t("reasons.empty")}
          </Typography>
          <Button variant="outlined" onClick={openAdd}>
            {t("reasons.addButton")}
          </Button>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("reasons.columns.order")}</TableCell>
                  <TableCell>{t("reasons.columns.value")}</TableCell>
                  <TableCell>{t("reasons.columns.labels")}</TableCell>
                  <TableCell>{t("reasons.columns.active")}</TableCell>
                  <TableCell align="right">{t("reasons.columns.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((r, i) => (
                  <TableRow key={r._id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                        {r.value}
                      </Typography>
                      <Typography variant="body2">{r.labels.en}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {summary(r)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={r.active}
                        onChange={() => handleToggle(r)}
                        inputProps={{ "aria-label": `${t("reasons.activeLabel")} ${r.value}` }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("reasons.moveUp")}>
                        <span>
                          <IconButton
                            aria-label={`${t("reasons.moveUp")} ${r.value}`}
                            disabled={i === 0 || reordering}
                            onClick={() => handleMove(i, -1)}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("reasons.moveDown")}>
                        <span>
                          <IconButton
                            aria-label={`${t("reasons.moveDown")} ${r.value}`}
                            disabled={i === items.length - 1 || reordering}
                            onClick={() => handleMove(i, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t("reasons.edit")}>
                        <IconButton
                          aria-label={`${t("reasons.edit")} ${r.value}`}
                          onClick={() => openEdit(r)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ReasonFormDialog
        open={dialogOpen}
        reason={editing}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
